import useBookingStore, {
  BookingMethod,
  PaymentMethods,
} from "../store/booking-store";
import { useTranslations } from "../../i18n";
import toast from "react-hot-toast";
import { useState } from "react";
import { mutate } from "swr";
import { useViewStore } from "@/store/view-store";
import { api } from "@/lib/api";
import { useAuthStore } from "@/store/auth-store";
import { useStudioStore } from "@/store/studio-store";

export function useCheckoutPayment() {
  const { onBookingSuccess } = useViewStore();
  const t = useTranslations("checkout");
  const { user } = useAuthStore();
  const studio = useStudioStore((state) => state.studio);

  const {
    selectedEvent,
    selectedPass,
    selectedUserPass,
    bookingMethod,
    paymentMethod,
    participants = [],
    discountInfo,
  } = useBookingStore();

  const [isLoading, setIsLoading] = useState(false);

  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [bookingId, setBookingId] = useState<string | null>(null);
  const [twintPendingBookingId, setTwintPendingBookingId] = useState<
    string | null
  >(null);

  /* ------------------------ HELPERS ------------------------ */

  const nonFirstTimeParticipants = participants.filter(
    (p) => !p.isFirstTime,
  ).length;

  const totalParticipants = Math.max(participants.length, 1);

  const isBookingValid = () => {
    if (
      bookingMethod === BookingMethod.DROP_IN &&
      !selectedPass &&
      !selectedUserPass
    ) {
      return false;
    }

    if (bookingMethod === BookingMethod.USER_PASS && selectedUserPass) {
      if (
        selectedUserPass.creditsInitial === null &&
        nonFirstTimeParticipants > 1
      )
        return false;

      if (
        selectedUserPass.creditsRemaining !== null &&
        selectedUserPass.creditsRemaining < nonFirstTimeParticipants
      )
        return false;
    }

    if (bookingMethod === BookingMethod.NEW_PASS && selectedPass) {
      if (selectedPass.credits === null && nonFirstTimeParticipants > 1)
        return false;

      if (
        selectedPass.credits !== null &&
        selectedPass.credits !== 1 &&
        selectedPass.credits < nonFirstTimeParticipants
      )
        return false;
    }

    return true;
  };

  const getTotalPrice = () => {
    if (!selectedEvent) return 0;

    if (bookingMethod === BookingMethod.USER_PASS) return 0;

    if (bookingMethod === BookingMethod.NEW_PASS && selectedPass) {
      return selectedPass.credits === 1
        ? selectedPass.price * totalParticipants
        : selectedPass.price;
    }

    if (bookingMethod === BookingMethod.DROP_IN) {
      const price = selectedEvent.dropInPrice || 0;

      return price * totalParticipants;
    }

    return 0;
  };

  const totalPrice = discountInfo
    ? Number(discountInfo.newAmount)
    : getTotalPrice();

  const requiresPayment =
    totalPrice > 0 && bookingMethod !== BookingMethod.USER_PASS;

  /* ------------------------ PAYMENT ------------------------ */
  const pay = async () => {
    try {
      if (!user) {
        toast.error(t("pleaseLogin"));
        return;
      }

      if (!selectedEvent || !studio) {
        toast.error(t("missingInformation"));
        return;
      }

      if (!isBookingValid()) {
        toast.error(t("bookingNotValid"));
        return;
      }

      setIsLoading(true);

      // ⚠️ Do NOT include main user in participants
      const filteredParticipants = participants.filter((p) => p.id !== user.id);

      /**
       * =====================================================
       * CASE 1 — USER PASS (credits are enough → direct booking)
       * =====================================================
       */
      if (
        (bookingMethod === BookingMethod.USER_PASS && selectedUserPass) ||
        selectedPass?.price === 0
      ) {
        const bookingPayload = {
          classSessionId: selectedEvent.id,
          bookingMethod,
          paymentMethod: PaymentMethods.PASS,
          studioId: studio.id,
          subscriptionId: selectedUserPass?.id,
          participants: filteredParticipants,
          passTypeId: selectedPass?.id,
        };

        const data = await api("/api/widget/bookings", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(bookingPayload),
        });

        toast.success(t("bookingSuccessful"));
        onBookingSuccess(data.bookingId);
        return;
      }

      /**
       * =====================================================
       * CASE 2 — DROP-IN or NEW PASS → checkout flow
       * =====================================================
       */
      if (requiresPayment && !paymentMethod) {
        toast.error(t("selectPaymentMethod"));
        return;
      }

      const checkoutPayload = {
        eventId: selectedEvent.id,
        bookingMethod,
        paymentMethod: paymentMethod,
        organizationId: studio.id,
        participants: filteredParticipants,
        ...(discountInfo && discountInfo.type !== "GIFT_CARD"
          ? { discountCodeId: discountInfo.id }
          : {}),
        ...(discountInfo?.type === "GIFT_CARD"
          ? { giftCardCode: discountInfo.code }
          : {}),
        ...(selectedPass ? { passTypeId: selectedPass.id } : {}),
      };

      const data = await api("/api/widget/bookings/create-checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(checkoutPayload),
      });

      // 📱 TWINT — booking is created pending manual payment verification.
      // Show the QR code / phone number screen before going to the profile.
      if (!data.requiresPayment && paymentMethod === PaymentMethods.TWINT) {
        setTwintPendingBookingId(data.bookingId);
        return;
      }

      // ✅ Free checkout (e.g. free class, free trial, 100% discount)
      if (!data.requiresPayment) {
        toast.success(t("bookingSuccessful"));
        onBookingSuccess(data.bookingId);
        return;
      }

      // 💳 Stripe redirect
      if (data.requiresPayment && data.clientSecret) {
        setClientSecret(data.clientSecret);
        setBookingId(data.bookingId);
        // Optional: scroll to the accordion / payment section
        return;
      }

      throw new Error("Missing checkout URL");
    } catch (error: any) {
      toast.error(error.message || t("somethingWentWrong"));
      console.error("Checkout error:", error);
    } finally {
      setIsLoading(false);
      mutate("/api/widget/profile/bookings");
    }
  };

  const confirmTwintPayment = () => {
    if (!twintPendingBookingId) return;
    toast.success(t("bookingSuccessful"));
    onBookingSuccess(twintPendingBookingId);
    setTwintPendingBookingId(null);
  };

  /* ------------------------ UI HELPERS ------------------------ */

  const buttonLabel = () => {
    if (!requiresPayment) return t("confirmBooking");

    if (!paymentMethod) return t("selectPayment");

    switch (paymentMethod) {
      case PaymentMethods.CARD:
        return t("payNow");
      case PaymentMethods.CASH:
        return t("payInStudio");
      case PaymentMethods.TRANSFER:
        return t("completeWithBankTransfer");
      case PaymentMethods.TWINT:
        return t("payWithTwint");
      default:
        return t("completePayment");
    }
  };

  return {
    pay,
    isLoading,
    isBookingValid,
    requiresPayment,
    totalPrice,
    buttonLabel,
    clientSecret,
    setClientSecret,
    bookingId,
    setBookingId,
    twintPendingBookingId,
    confirmTwintPayment,
  };
}
